/* Northsheep — a themed dropdown for "Sort by".
 *
 * The native <select> cannot be styled where it matters: the closed box takes
 * CSS, but the OPEN list is drawn by the operating system, so it arrives as a
 * white panel with a blue highlight in the middle of a warm ivory page. That
 * is the part the owner objected to, and no amount of CSS reaches it.
 *
 * So the real select stays in the DOM and keeps doing its job - Dawn listens
 * for its change event to re-render the grid, and a browser without JS still
 * gets a working control - while a button and a list are drawn beside it and
 * simply write back to it.
 *
 * SURVIVING THE RE-RENDER. Sorting re-renders the whole facets region through
 * the Section Rendering API, which destroys this UI along with the select it
 * was built from (the trap in CLAUDE.md). Nothing is cached across that: the
 * observer rebuilds from whatever select is in the DOM now, and every listener
 * is delegated from document, so there is nothing to re-attach.
 */
(function () {
  if (window.__nsSortSelect) return;
  window.__nsSortSelect = true;

  var OPEN = 'ns-sort--open';

  function build(select) {
    if (!select || select.__nsSortDone) return;
    select.__nsSortDone = true;

    var wrap = document.createElement('div');
    wrap.className = 'ns-sort';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ns-sort__button';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');

    var label = document.createElement('span');
    label.className = 'ns-sort__value';
    btn.appendChild(label);

    var caret = document.createElement('span');
    caret.className = 'ns-sort__caret';
    caret.setAttribute('aria-hidden', 'true');
    btn.appendChild(caret);

    var list = document.createElement('ul');
    list.className = 'ns-sort__list';
    list.setAttribute('role', 'listbox');

    for (var i = 0; i < select.options.length; i++) {
      var o = select.options[i];
      var li = document.createElement('li');
      li.className = 'ns-sort__option';
      li.setAttribute('role', 'option');
      li.setAttribute('data-value', o.value);
      li.setAttribute('tabindex', '-1');
      li.textContent = o.textContent.trim();
      if (o.selected) {
        li.setAttribute('aria-selected', 'true');
        label.textContent = li.textContent;
      } else {
        li.setAttribute('aria-selected', 'false');
      }
      list.appendChild(li);
    }
    if (!label.textContent) label.textContent = (select.options[0] || {}).textContent || '';

    wrap.appendChild(btn);
    wrap.appendChild(list);
    select.parentNode.insertBefore(wrap, select);

    /* The select is kept, not removed: Dawn's facet code reads and listens to
       it. Hidden from sight and from the tab order, because the button is now
       the control a person uses. */
    select.classList.add('ns-sort__native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');
  }

  function closeAll(except) {
    var open = document.querySelectorAll('.' + OPEN);
    for (var i = 0; i < open.length; i++) {
      if (open[i] === except) continue;
      open[i].classList.remove(OPEN);
      var b = open[i].querySelector('.ns-sort__button');
      if (b) b.setAttribute('aria-expanded', 'false');
    }
  }

  function pick(wrap, value, text) {
    var select = wrap.parentNode.querySelector('select.facet-filters__sort');
    if (!select) return;
    var v = wrap.querySelector('.ns-sort__value');
    if (v) v.textContent = text;
    var opts = wrap.querySelectorAll('.ns-sort__option');
    for (var i = 0; i < opts.length; i++) {
      opts[i].setAttribute('aria-selected',
        opts[i].getAttribute('data-value') === value ? 'true' : 'false');
    }
    if (select.value === value) return;
    select.value = value;
    /* THE EVENTS A REAL SELECTION FIRES, IN ITS ORDER: input, then change.

       Dawn's facet form listens for INPUT (facets.js: facetForm.addEventListener
       ('input', …)). This used to send only `change`, so on a desktop, where
       this button replaces the native select, picking a sort updated the
       label and the hidden select and nothing else — no request, no new
       order, no ?sort_by in the address (found in the 23 Sep 2026 audit).
       Phones never noticed: the drawer keeps the plain select. Bubbling, or
       it never reaches the form. */
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('click', function (e) {
    var opt = e.target.closest ? e.target.closest('.ns-sort__option') : null;
    if (opt) {
      var w = opt.closest('.ns-sort');
      pick(w, opt.getAttribute('data-value'), opt.textContent.trim());
      w.classList.remove(OPEN);
      var b = w.querySelector('.ns-sort__button');
      if (b) { b.setAttribute('aria-expanded', 'false'); b.focus(); }
      return;
    }
    var btn = e.target.closest ? e.target.closest('.ns-sort__button') : null;
    if (btn) {
      var wrap = btn.closest('.ns-sort');
      var isOpen = wrap.classList.contains(OPEN);
      closeAll(wrap);
      wrap.classList.toggle(OPEN, !isOpen);
      btn.setAttribute('aria-expanded', String(!isOpen));
      return;
    }
    closeAll(null);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      /* Back to the button when the key was pressed inside the list (audit,
         23 Sep 2026): focus used to stay on an option of the list it had just
         closed — an invisible element, so the next Tab started nowhere. */
      var from = e.target && e.target.closest ? e.target.closest('.ns-sort') : null;
      closeAll(null);
      var back = from && from.querySelector('.ns-sort__button');
      if (back && from.contains(document.activeElement) && back !== document.activeElement) back.focus();
      return;
    }
    var wrap = e.target.closest ? e.target.closest('.ns-sort') : null;
    if (!wrap) return;
    if (e.key === 'Enter' || e.key === ' ') {
      var opt = e.target.closest('.ns-sort__option');
      if (opt) {
        e.preventDefault();
        opt.click();
      }
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    var opts = Array.prototype.slice.call(wrap.querySelectorAll('.ns-sort__option'));
    if (!wrap.classList.contains(OPEN)) {
      wrap.classList.add(OPEN);
      wrap.querySelector('.ns-sort__button').setAttribute('aria-expanded', 'true');
    }
    var here = opts.indexOf(document.activeElement);
    var next = e.key === 'ArrowDown' ? here + 1 : here - 1;
    if (next < 0) next = 0;
    if (next >= opts.length) next = opts.length - 1;
    if (here === -1) {
      next = opts.findIndex(function (o) { return o.getAttribute('aria-selected') === 'true'; });
      if (next < 0) next = 0;
    }
    opts[next].focus();
  });

  function scan() {
    var selects = document.querySelectorAll('select.facet-filters__sort');
    for (var i = 0; i < selects.length; i++) build(selects[i]);
  }

  function init() {
    scan();
    if ('MutationObserver' in window) {
      var t = null;
      new MutationObserver(function () {
        window.clearTimeout(t);
        t = window.setTimeout(scan, 60);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
